import type {
  IInfoPanelAction,
  IInfoPanelGroup,
  IInfoPanelItem,
  IInfoPanelSearchOptions,
} from '../types';
import type { BoxProps } from '@shopify/polaris';

import { Box, BlockStack, Button, InlineStack, Icon, Text } from '@shopify/polaris';
import { GInfoPanelSingle } from './GInfoPanelSingle';
import { SearchIcon } from '@shopify/polaris-icons';
import { GInfoPanelGroup } from './GInfoPanelGroup';
import { GTextField } from '~/presentation/ui';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import styles from '../GInfoPanel.module.css';

export interface GInfoPanelLeftColumnProps {
  items: IInfoPanelItem[];
  groups: IInfoPanelGroup[];
  selectedId?: string;
  selectedBackground?: BoxProps['background'];
  searchQuery: string;
  search?: IInfoPanelSearchOptions;
  searchEmpty?: string;
  hasNoResults?: boolean;
  emptyState?: React.ReactNode;
  primaryAction?: IInfoPanelAction;
  secondaryAction?: IInfoPanelAction;
  onSelect: (id: string) => void;
  onHover: (id: string) => void;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
}

export const GInfoPanelLeftColumn = ({
  items,
  groups,
  selectedId,
  selectedBackground,
  searchQuery,
  search,
  searchEmpty,
  hasNoResults,
  emptyState,
  primaryAction,
  secondaryAction,
  onSelect,
  onHover,
  onSearchChange,
}: GInfoPanelLeftColumnProps) => {
  const { t } = useTranslation();

  const hasActions = primaryAction || secondaryAction;

  const markupEmptyState = useMemo(() => {
    if (emptyState) {
      return emptyState;
    }

    return (
      <Box paddingInline="150">
        <Box padding="150">
          <Text as="p" variant="bodyMd">
            {searchEmpty ?? t('No result found')}
          </Text>
        </Box>
      </Box>
    );
  }, [emptyState, searchEmpty, t]);

  return (
    <div className={styles.FlexColumn}>
      <BlockStack gap="0">
        {search?.enabled && (
          <div className={styles.StickyTop}>
            <Box padding="300" background="bg-surface" borderBlockEndWidth="0" borderColor="border">
              <GTextField
                value={searchQuery}
                prefix={<Icon source={SearchIcon} />}
                onChange={onSearchChange}
                placeholder={search.placeholder}
                // clearButton
                // onClearButtonClick={onClearSearch}
              />
            </Box>
          </div>
        )}

        <div className={styles.ScrollableContainer}>
          {hasNoResults ? (
            <>{markupEmptyState}</>
          ) : (
            <>
              <GInfoPanelSingle
                items={items}
                selectedId={selectedId}
                selectedBackground={selectedBackground}
                onSelect={onSelect}
                onHover={onHover}
              />

              <GInfoPanelGroup
                isSearching={searchQuery.length > 0}
                groups={groups}
                search={search}
                selectedId={selectedId}
                selectedBackground={selectedBackground}
                onSelect={onSelect}
                onHover={onHover}
              />
            </>
          )}
        </div>

        {hasActions && (
          <div className={styles.StickyBottom}>
            <Box
              padding="300"
              background="bg-surface"
              borderBlockStartWidth="025"
              borderColor="border"
            >
              <InlineStack gap="200" align="end">
                {secondaryAction && (
                  <Button
                    onClick={secondaryAction.onAction}
                    disabled={secondaryAction.disabled}
                    loading={secondaryAction.loading}
                  >
                    {secondaryAction.content}
                  </Button>
                )}
                {primaryAction && (
                  <Button
                    variant="primary"
                    onClick={primaryAction.onAction}
                    disabled={primaryAction.disabled}
                    loading={primaryAction.loading}
                  >
                    {primaryAction.content}
                  </Button>
                )}
              </InlineStack>
            </Box>
          </div>
        )}
      </BlockStack>
    </div>
  );
};
